import React, { useContext, useState } from 'react';
import { ConfirmDialog } from '../../Common/dialog/ConfirmDialog';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { API_CONSTANTS, isCreditsAvailable, showCredits } from '../../utils';
import { CustomDialogContainer, CustomDialogTitle } from 'apps/t2c-fe/Common/dialog';
import { Alert, Button, Checkbox, DialogContent, Skeleton } from '@mui/material';
import { Styles } from '@t2c/shared-fe';
import LoadingButton from '@mui/lab/LoadingButton';
import { useHandleApiCall } from '../../hooks';
import { AppContext } from '../../context/AppContext';
import { Iconify } from '@sdf/base';
import { CreditContext } from 'apps/t2c-fe/context/CreditProvider';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import { AddonCreditsModal } from '../addon-credits-modal/AddonCreditsModal';

interface CreditDialogProps {
  isOpen: boolean;
  creditDetail?: {
    availableCredit: number;
    creditNeeded: number;
    creditsInUse?: number;
  };
  onClose: () => void;
  onConfirm?: () => void;
  isFetchingCreditDetail: boolean;
  onUpgrade: () => void;
  isLoading?: boolean;
  enoughCreditConfirmBtnTitle?: string;
  enoughCreditModalTitle?: string;
  upgradeButtonTitle?: string;
  promptRestriction?: boolean;
  isTrain?: boolean;
}

export const CreditDialog: React.FC<CreditDialogProps> = ({
  isOpen,
  creditDetail,
  onClose,
  onConfirm,
  isFetchingCreditDetail,
  onUpgrade,
  isLoading,
  enoughCreditConfirmBtnTitle = 'Render',
  enoughCreditModalTitle = 'Confirm Render',
  upgradeButtonTitle = 'Save & Upgrade',
  promptRestriction = false,
  isTrain = false,
}) => {
  const isEnoughCredit = isCreditsAvailable(
    creditDetail?.availableCredit,
    creditDetail?.creditsInUse,
    creditDetail?.creditNeeded
  );
  const title = isEnoughCredit ? enoughCreditModalTitle : upgradeButtonTitle;

  const { patch } = useHandleApiCall();
  const { setShowCreditDialog } = useContext(AppContext);
  const { isUserSubscribed } = useContext(CreditContext);
  const [isAddCreditOpen, setIsAddCreditOpen] = useState(false);
  const updateUserMutation = patch(API_CONSTANTS.UPDATE_USER.key, API_CONSTANTS.UPDATE_USER.path);
  const [checked, setChecked] = useState(false);
  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const updateData = { showCreditDialog: !event.target.checked };
      const res = await updateUserMutation.mutateAsync(updateData);
      setChecked(!res.showCreditDialog);
      setShowCreditDialog(res.showCreditDialog);
    } catch (error) {
      console.log(error);
    }
  };

  const openAddCredits = () => {
    setIsAddCreditOpen(true);
  };

  const closeAddCredits = () => {
    onClose();
    setIsAddCreditOpen(false);
  };

  return (
    <CustomDialogContainer open={isOpen} maxWidth="sm">
      <CustomDialogTitle sx={styles.title} title={title} onClose={onClose} />
      <DialogContent dividers sx={styles.dialogContent}>
        <AddonCreditsModal openModal={isAddCreditOpen} closeModal={closeAddCredits} />

        <Stack rowGap={4}>
          <Stack rowGap={4}>
            <Stack sx={styles.creditContainer} mt={2}>
              {!promptRestriction && (
                <>
                  <Typography sx={{ ...styles.boldText, display: 'flex', alignItems: 'center' }}>
                    Credits Available:{' '}
                    {isFetchingCreditDetail ? (
                      <Skeleton width={50} sx={{ display: 'inline-block', mx: 1 }} />
                    ) : (
                      showCredits(creditDetail?.availableCredit - creditDetail?.creditsInUse) || '0'
                    )}
                  </Typography>
                  <Typography sx={{ ...styles.grayText, display: 'flex', alignItems: 'center' }}>
                    This action will cost{' '}
                    {isFetchingCreditDetail ? (
                      <Skeleton width={50} sx={{ display: 'inline-block', mx: 1 }} />
                    ) : (
                      showCredits(creditDetail?.creditNeeded) || '0'
                    )}{' '}
                    credits.
                  </Typography>
                  {isTrain && (
                    <Typography
                      sx={{
                        ...styles.grayText,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontSize: '12px',
                      }}
                    >
                      <Iconify
                        icon={'heroicons-outline:information-circle'}
                        width={'20px'}
                        height={'20px'}
                      />
                      Cost shown includes one-time training of the selected avatar with this model.
                    </Typography>
                  )}
                </>
              )}
              {promptRestriction && (
                <>
                  <Typography
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      maxWidth: '90%',
                    }}
                  >
                    You've reached the free trial limit for this feature. Upgrade to a paid plan to
                    continue.
                  </Typography>
                </>
              )}
            </Stack>
          </Stack>

          {/* {!isFetchingCreditDetail && errorFetchingCredits && (
              <Stack>
                <Typography color="error.main" textAlign={'center'}>
                  We couldn't fetch your credit data at the moment. Please try again.
                </Typography>
              </Stack>
            )} */}
          {/* <Typography sx={styles.noteText}>
              This is an approximate calculation, the actual credits will depend on the duration
              of the output audio.
            </Typography> */}
          {!promptRestriction && isEnoughCredit && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Checkbox
                checked={checked}
                onChange={handleCheckboxChange}
                inputProps={{ 'aria-label': 'controlled' }}
              />
              <Typography>Don't Show Again</Typography>
            </Stack>
          )}

          {!isEnoughCredit && (
            <Alert
              icon={<TipsAndUpdatesIcon fontSize="inherit" />}
              severity="info"
              sx={{
                color: (theme) => theme.palette.text.placeholder,
                background: (theme) => theme.palette.background.activeCard,
                '& .MuiAlert-icon': {
                  color: (theme) => theme.palette.text.coloredText,
                },
              }}
            >
              It looks like you don't have enough credits for this generation. Top up your balance
              to bring your idea to life!{' '}
              {creditDetail?.availableCredit !== 0 &&
                `(Extra Credits requred: ${showCredits(
                  creditDetail?.creditNeeded - creditDetail?.availableCredit
                )})`}
            </Alert>
          )}
          <Stack direction="row" justifyContent={'space-between'}>
            <Button variant="outlined" sx={styles.cancelBtn} onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            {isFetchingCreditDetail ? (
              <Skeleton width={120} height={70} sx={{ borderRadius: 4 }} />
            ) : !isEnoughCredit ? (
              <LoadingButton
                variant="contained"
                sx={styles.generateBtn}
                onClick={isUserSubscribed ? openAddCredits : onUpgrade}
                loading={isFetchingCreditDetail || isLoading}
              >
                {isUserSubscribed ? 'Add on Plans' : upgradeButtonTitle}
              </LoadingButton>
            ) : (
              <LoadingButton
                variant="contained"
                sx={styles.generateBtn}
                onClick={onConfirm}
                loading={isFetchingCreditDetail || isLoading}
              >
                {enoughCreditConfirmBtnTitle}
              </LoadingButton>
            )}
          </Stack>
        </Stack>
      </DialogContent>
    </CustomDialogContainer>
  );
};

const styles: Styles = {
  title: {
    color: (theme) => theme.palette.text.primary,
    paddingBottom: '10px',
    paddingTop: '10px',
  },
  dialogContent: {
    padding: '16px',
    background: (theme) => theme.palette.background.secondary,
  },
  cancelBtn: {
    color: (theme) => theme.palette.text.primary,
    borderColor: (theme) => theme.palette.background.border,
    fontWeight: '500',
    paddingX: 3,
  },
  generateBtn: {
    color: (theme) => theme.palette.text.primary,
    boxShadow: 'none',
    fontWeight: '500',
    paddingX: 4,
  },
  boldText: {
    fontWeight: 600,
  },
  grayText: {
    color: (theme) => theme.palette.text.placeholder,
  },
  creditContainer: {
    backgroundColor: (theme) => theme.palette.background.activeCard,
    backgroundImage: "url('/assets/icons/UpgradeDiamondIcon.svg')",
    backgroundRepeat: 'no-repeat',
    backgroundSize: '6%',
    backgroundPosition: 'calc(100% - 10px) center',
    padding: 2,
    borderRadius: 1,
  },
  noteText: {
    color: (theme) => theme.palette.text.primary,
    fontSize: 16,
  },
};
